import jsPDF from "jspdf";
import QRCode from "qrcode";
import { ensureArabicFont, pdfText, hasArabic } from "@/lib/pdfFonts";
import { pdfStr } from "@/lib/pdfStrings";
import { fmtMoney } from "@/lib/money";

/* ==========================================================================
 *  Meos Cargo — shared branded PDF theme
 *  --------------------------------------------------------------------------
 *  All company reports (invoices, statements, delivery receipts, master
 *  manifests) render inside the same visual frame so customers get one
 *  recognisable identity across every document.
 * ==========================================================================
 */
export const BRAND = {
  navy: [17, 45, 108],       // primary   ·  #112D6C
  navyDark: [10, 24, 60],    // headings  ·  #0A183C
  navySoft: [235, 240, 250], // panels    ·  #EBF0FA
  red: [220, 38, 38],        // accent    ·  #DC2626
  redSoft: [252, 231, 231],
  grey: [100, 116, 139],     // secondary text
  greyLight: [148, 163, 184],
  zebra: [248, 250, 252],
  divider: [222, 226, 234],
  ink: [17, 24, 39],
};

export const hexToRgb = (hex) => {
  const h = String(hex || "#112D6C").replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

export const loadLogo = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = "/logo.png";
  });

/** Draw an icon-less contact row (📍 phone · address · email · web). */
function drawContactBlock(doc, emisor, x, y, w, lang) {
  const S = pdfStr(lang);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...BRAND.navyDark);
  pdfText(doc, emisor.razon_social || "MEOS CARGO", x + w, y, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.grey);
  const rows = [
    emisor.direccion,
    emisor.telefono,
    emisor.email,
    emisor.web,
    emisor.cif ? `${S.tax_id || "NIF"}: ${emisor.cif}` : null,
  ].filter(Boolean);
  rows.forEach((r, i) => {
    pdfText(doc, r, x + w, y + 14 + i * 11, { align: "right", maxWidth: w });
  });
}

/**
 * Draws the branded frame (header + watermark + curved footer) around any
 * report. Returns the Y at which the report body may start.
 */
export async function drawBrandedFrame(doc, { emisor = {}, estilo = {}, lang = "es", subtitle = "", showWatermark = true }) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 34;
  const showLogo = estilo.mostrar_logo !== false;
  const logoW = Math.max(90, Math.min(180, Number(estilo.logo_size) || 130));

  // ---- Header ----
  if (showLogo) {
    const img = await loadLogo();
    if (img) {
      const ratio = img.height / img.width || 0.45;
      const h = Math.min(70, logoW * ratio);
      try { doc.addImage(img, "PNG", M, 30, logoW, h); }
      catch (e) { console.warn("invoicePdf: logo embed failed:", e); }
      // Tagline under the logo
      const S = pdfStr(lang);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(...BRAND.navy);
      pdfText(doc, (estilo.tagline || S.brand_tagline || "SOLUCIONES QUE MUEVEN TU MUNDO"), M, 30 + h + 12);
    }
  }
  drawContactBlock(doc, emisor, W - M - 220, 34, 220, lang);

  // Divider under header
  doc.setDrawColor(...BRAND.divider);
  doc.setLineWidth(0.7);
  doc.line(M, 120, W - M, 120);

  // Optional subtitle inline (used by non-invoice reports)
  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.grey);
    pdfText(doc, subtitle, M, 138);
  }

  // ---- Watermark ----
  if (showWatermark) {
    doc.saveGraphicsState?.();
    // saveGraphicsState/GState are only present when jsPDF was built with the
    // extended graphics plugin. Guard the calls silently so PDFs render on
    // both jsPDF flavours; the watermark just falls back to solid ink.
    try { doc.setGState?.(doc.GState?.({ opacity: 0.06 })); } catch { /* graphics plugin absent — draw solid */ }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(120);
    doc.setTextColor(...BRAND.navy);
    pdfText(doc, "M.E.O.S", W / 2, H - 160, { align: "center" });
    try { doc.setGState?.(doc.GState?.({ opacity: 1 })); } catch { /* same as above — no-op */ }
    doc.restoreGraphicsState?.();
  }

  // ---- Curved footer band ----
  drawFooterBand(doc, { emisor, estilo, lang });

  return subtitle ? 160 : 148;
}

/** Curved red arc + navy footer with copyright / tagline strip. */
export function drawFooterBand(doc, { emisor = {}, estilo = {}, lang = "es" }) {
  const S = pdfStr(lang);
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // Red curved arc (approximated with a filled rectangle + ellipse strip)
  doc.setFillColor(...BRAND.red);
  doc.rect(0, H - 66, W, 6, "F");
  // Slight rounded triangle over the strip for the "wave"
  doc.setFillColor(...BRAND.red);
  doc.triangle(0, H - 66, W * 0.55, H - 90, W * 0.05, H - 66, "F");

  // Navy footer bar
  doc.setFillColor(...BRAND.navyDark);
  doc.rect(0, H - 60, W, 60, "F");

  // Footer text
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  pdfText(doc, emisor.razon_social || "M.E.O.S", W - 34, H - 34, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(203, 213, 225);
  pdfText(doc, estilo.pie || S.brand_slogan || "Comprometidos con tu crecimiento", W - 34, H - 20, { align: "right" });
  pdfText(doc, `${S.printed_on} ${new Date().toLocaleDateString()}`, 34, H - 20);
}

/** Simple pill helper — draws a rounded rectangle with a label + value. */
function pill(doc, { x, y, w, h, label, value, fill = BRAND.navyDark, fg = [255, 255, 255], radius = 6, labelSize = 9, valueSize = 12 }) {
  doc.setFillColor(...fill);
  doc.roundedRect(x, y, w, h, radius, radius, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(labelSize);
  doc.setTextColor(...fg);
  pdfText(doc, label, x + 14, y + h / 2 + 3);
  doc.setFontSize(valueSize);
  pdfText(doc, value, x + w - 14, y + h / 2 + 3, { align: "right" });
}

/** QR code embedded as PNG. */
async function embedQR(doc, text, x, y, size) {
  if (!text) return;
  try {
    const url = await QRCode.toDataURL(text, { errorCorrectionLevel: "M", margin: 0, width: size * 2 });
    doc.addImage(url, "PNG", x, y, size, size);
  } catch (e) { console.warn("QR embed failed", e); }
}

/**
 * Professional A4 invoice — matches the branded MEOS template
 *   ┌──── logo + tagline ───────────── company contact ────┐
 *   │  FACTURA / Comprobante Fiscal           [Folio pill]  │
 *   │  Datos del cliente panel                  [meta rows] │
 *   │  Items table (navy header, zebra rows)                │
 *   │                             Subtotal / Descuento / IVA│
 *   │                                    Total (navy pill)  │
 *   │  Observaciones panel                                  │
 *   │  QR ─────────────  Autorizado por: MEOS               │
 *   ├──────── red wave · navy footer ──────────────────────┤
 */
export async function invoicePDF(inv, lang = "es", currency = "EUR", settings = {}) {
  const S = pdfStr(lang);
  const estilo = settings.estilo || {};
  const emisor = inv.emisor || settings.empresa || {};
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 34;
  const money = (v) => fmtMoney(v, currency);

  const dynamic = [
    inv.cliente_nombre, inv.cliente_direccion, inv.notas, inv.remarks,
    emisor.razon_social, emisor.direccion, estilo.pie,
    ...(inv.lineas || []).map((l) => l.descripcion),
  ];
  if (lang === "ar" || dynamic.some(hasArabic)) await ensureArabicFont(doc);

  await drawBrandedFrame(doc, { emisor, estilo, lang });

  // ---- Title block ----
  let y = 165;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(38);
  doc.setTextColor(...BRAND.navyDark);
  pdfText(doc, S.invoice_title.toUpperCase(), M, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(13);
  doc.setTextColor(...BRAND.grey);
  pdfText(doc, S.invoice_subtitle || S.invoice_title, M, y + 20);

  // Folio pill (right)
  pill(doc, {
    x: W - M - 260, y: y - 24, w: 260, h: 36,
    label: `${S.folio || "Folio"}:`, value: inv.numero || "—",
  });

  // Meta rows (right, below folio)
  const meta = [
    [S.issue_date || S.date, inv.fecha || "—"],
    [S.due_date, inv.vencimiento || "—"],
    [S.payment_method || "Método de pago", inv.metodo_pago || S.bank_transfer || "Transferencia bancaria"],
    [S.currency || "Moneda", currency],
  ];
  const metaX = W - M - 260;
  let metaY = y + 26;
  doc.setFontSize(9.5);
  meta.forEach(([k, v]) => {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...BRAND.navyDark);
    pdfText(doc, `${k}:`, metaX, metaY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...BRAND.ink);
    pdfText(doc, String(v), W - M, metaY, { align: "right" });
    metaY += 15;
  });

  // ---- Client panel ----
  y += 46;
  const panelH = 70;
  doc.setFillColor(...BRAND.navySoft);
  doc.roundedRect(M, y, W - M * 2 - 280, panelH, 8, 8, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.navy);
  pdfText(doc, (S.client_data || "Datos del cliente").toUpperCase(), M + 16, y + 18);
  const clientRows = [
    [S.name || "Nombre", inv.cliente_nombre || "—"],
    [S.tax_id || "RFC", inv.cliente_tax_id || "—"],
    [S.address || "Dirección", inv.cliente_direccion || "—"],
  ];
  doc.setFontSize(9);
  clientRows.forEach(([k, v], i) => {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...BRAND.grey);
    pdfText(doc, `${k}:`, M + 16, y + 34 + i * 14);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...BRAND.ink);
    pdfText(doc, String(v), M + 90, y + 34 + i * 14, { maxWidth: W - M * 2 - 280 - 110 });
  });

  // ---- Items table ----
  y += panelH + 22;
  const rtl = lang === "ar";
  // Column X positions. In RTL the table is mirrored so No/Descripción
  // land on the right side of the sheet.
  const cols = rtl
    ? { no: W - M - 14, desc: W - M - 40, qty: M + 230, unit: M + 140, imp: M + 14 }
    : { no: M + 14, desc: M + 40, qty: W - M - 230, unit: W - M - 140, imp: W - M - 14 };
  const descAlignRight = rtl;
  doc.setFillColor(...BRAND.navyDark);
  doc.roundedRect(M, y, W - M * 2, 26, 4, 4, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  pdfText(doc, "No.", cols.no, y + 17, rtl ? { align: "right" } : {});
  pdfText(doc, S.description, cols.desc, y + 17, rtl ? { align: "right" } : {});
  pdfText(doc, S.quantity, cols.qty, y + 17, { align: rtl ? "left" : "right" });
  pdfText(doc, S.unit_price, cols.unit, y + 17, { align: rtl ? "left" : "right" });
  pdfText(doc, S.amount || S.total, cols.imp, y + 17, { align: rtl ? "left" : "right" });

  y += 26;
  doc.setFontSize(9.5);
  const lines = inv.lineas || [];
  const descWidth = rtl ? (cols.desc - cols.qty - 10) : (cols.qty - cols.desc - 10);
  lines.forEach((l, i) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    const descText = l.descripcion && String(l.descripcion).trim() ? String(l.descripcion) : "—";
    const wrapped = doc.splitTextToSize(descText, Math.max(80, descWidth));
    const rowH = Math.max(22, 14 + wrapped.length * 11);
    if (i % 2 === 0) {
      doc.setFillColor(...BRAND.zebra);
      doc.rect(M, y, W - M * 2, rowH, "F");
    }
    doc.setTextColor(...BRAND.ink);
    pdfText(doc, String(i + 1), cols.no, y + 14, rtl ? { align: "right" } : {});
    wrapped.forEach((ln, j) => {
      pdfText(doc, ln, cols.desc, y + 14 + j * 11, {
        maxWidth: Math.max(80, descWidth),
        ...(descAlignRight ? { align: "right" } : {}),
      });
    });
    pdfText(doc, String(l.cantidad ?? ""), cols.qty, y + 14, { align: rtl ? "left" : "right" });
    pdfText(doc, money(l.precio_unitario), cols.unit, y + 14, { align: rtl ? "left" : "right" });
    pdfText(doc, money((+l.cantidad || 0) * (+l.precio_unitario || 0)), cols.imp, y + 14, { align: rtl ? "left" : "right" });
    y += rowH;
  });

  // ---- Totals ----
  y += 12;
  const totalsW = 260;
  const totalsX = rtl ? M : (W - M - totalsW);
  const totalsRightX = rtl ? (M + totalsW) : (W - M);
  // ---- Bank / IBAN panel on the opposite side (if available and allowed) ----
  const showIban = (estilo.mostrar_iban !== false) && (emisor.iban || emisor.banco);
  if (showIban) {
    const bankH = 70;
    const bankX = rtl ? (M + totalsW + 20) : M;
    const bankW = W - M * 2 - totalsW - 20;
    doc.setFillColor(...BRAND.redSoft);
    doc.roundedRect(bankX, y - 6, bankW, bankH, 8, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.red);
    pdfText(doc, (S.payment_info || "Datos de pago").toUpperCase(), bankX + 14, y + 10);
    doc.setFontSize(8.5);
    doc.setTextColor(...BRAND.ink);
    let by = y + 26;
    const labelX = bankX + 14;
    const valueX = bankX + 70;
    const valueMaxW = bankW - 80;
    if (emisor.banco) {
      doc.setFont("helvetica", "bold"); pdfText(doc, `${S.bank || "Banco"}:`, labelX, by);
      doc.setFont("helvetica", "normal"); pdfText(doc, emisor.banco, valueX, by, { maxWidth: valueMaxW });
      by += 13;
    }
    if (emisor.iban) {
      doc.setFont("helvetica", "bold"); pdfText(doc, "IBAN:", labelX, by);
      doc.setFont("courier", "normal"); pdfText(doc, emisor.iban, valueX, by, { maxWidth: valueMaxW });
      doc.setFont("helvetica", "normal");
      by += 13;
    }
    if (emisor.razon_social) {
      doc.setFont("helvetica", "bold"); pdfText(doc, `${S.beneficiary || "Titular"}:`, labelX, by);
      doc.setFont("helvetica", "normal"); pdfText(doc, emisor.razon_social, valueX, by, { maxWidth: valueMaxW });
    }
  }

  const rowT = (label, value, bold = false) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.navyDark);
    pdfText(doc, label, totalsX, y);
    doc.setTextColor(...BRAND.ink);
    pdfText(doc, value, totalsRightX, y, { align: "right" });
    y += 18;
  };
  rowT(S.subtotal, money(inv.subtotal), true);
  // Only show discount / VAT lines when non-zero (keeps the layout compact).
  if ((+inv.descuento || 0) > 0) rowT(S.discount || "Descuento", money(inv.descuento || 0));
  if ((+inv.iva || 0) > 0) rowT(`${S.vat} (${inv.iva_pct || 16}%)`, money(inv.iva), true);
  // Total pill (navy)
  pill(doc, {
    x: totalsX, y: y - 6, w: totalsW, h: 32,
    label: S.total, value: money(inv.total), fill: BRAND.navyDark, valueSize: 13,
  });
  y += 34;

  // ---- Observations (dynamic height, overflow onto page 2 if needed) ----
  const H = doc.internal.pageSize.getHeight();
  const notesText = (inv.remarks && String(inv.remarks).trim()) || (inv.notas && String(inv.notas).trim()) || S.thanks_note || "";
  if (notesText) {
    const obsFontSize = notesText.length > 500 ? 8 : notesText.length > 200 ? 8.5 : 9;
    const obsLineH = obsFontSize + 2.5;
    const obsPadding = 14;
    const obsMaxW = W - M * 2 - 32;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(obsFontSize);
    const wrappedObs = doc.splitTextToSize(notesText, obsMaxW);
    // Reserve 110pt below for QR+signature. If we overflow, break to page 2.
    const availableBottom = H - 110;
    const neededH = 28 + wrappedObs.length * obsLineH;
    const fits = (y + neededH) <= availableBottom;
    let obsLinesPage1 = wrappedObs;
    let obsLinesPage2 = [];
    if (!fits) {
      const linesCanFit = Math.max(1, Math.floor((availableBottom - y - 28) / obsLineH));
      obsLinesPage1 = wrappedObs.slice(0, linesCanFit);
      obsLinesPage2 = wrappedObs.slice(linesCanFit);
    }
    const obsH1 = 24 + obsLinesPage1.length * obsLineH;
    doc.setFillColor(...BRAND.navySoft);
    doc.roundedRect(M, y, W - M * 2, obsH1, 8, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.navy);
    pdfText(doc, (S.observations || "Observaciones") + ":", M + obsPadding, y + 15);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(obsFontSize);
    doc.setTextColor(...BRAND.ink);
    obsLinesPage1.forEach((ln, idx) => {
      pdfText(doc, ln, M + obsPadding, y + 28 + idx * obsLineH, { maxWidth: obsMaxW });
    });
    y += obsH1 + 12;

    if (obsLinesPage2.length) {
      doc.addPage();
      await drawBrandedFrame(doc, { emisor, estilo, lang, showWatermark: false });
      y = 160;
      const obsH2 = 24 + obsLinesPage2.length * obsLineH;
      doc.setFillColor(...BRAND.navySoft);
      doc.roundedRect(M, y, W - M * 2, obsH2, 8, 8, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...BRAND.navy);
      pdfText(doc, (S.observations || "Observaciones") + ": (…)", M + obsPadding, y + 15);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(obsFontSize);
      doc.setTextColor(...BRAND.ink);
      obsLinesPage2.forEach((ln, idx) => {
        pdfText(doc, ln, M + obsPadding, y + 28 + idx * obsLineH, { maxWidth: obsMaxW });
      });
      y += obsH2 + 12;
    }
  }

  // ---- QR + Signature (anchored to bottom of current page) ----
  const qrSize = 70;
  const sigBaseY = Math.min(y, H - 110);
  await embedQR(doc, inv.numero ? `INV:${inv.numero}` : "", M, sigBaseY, qrSize);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...BRAND.grey);
  pdfText(doc, S.cfdi_note || "Documento generado electrónicamente.", M, sigBaseY + qrSize + 10, { maxWidth: 160 });

  // Signature line (right)
  doc.setDrawColor(...BRAND.navyDark);
  doc.setLineWidth(0.7);
  doc.line(W - M - 200, sigBaseY + 44, W - M, sigBaseY + 44);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.navyDark);
  pdfText(doc, S.authorized_by || "Autorizado por:", W - M - 100, sigBaseY + 60, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  pdfText(doc, emisor.razon_social || "M.E.O.S", W - M - 100, sigBaseY + 76, { align: "center" });

  doc.save(`${inv.numero || "invoice"}.pdf`);
}

/* Backwards-compat aliases used by other pdf modules. */
export const drawHeader = async (doc, opts) => {
  await drawBrandedFrame(doc, opts);
  return 165;
};
export const drawFooter = () => {};
